import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { api_url } from "../../constant/constant";
import { showToast } from "../ShowToast/ToastNotification";
import "./DetailLicense.css";

const initialFormData = {
  customer_id: "",
  system_license_max_users: "",
  system_license_start_date: "",
  system_license_expiration_date: "",
  phishing_license_max_users: "",
  phishing_license_start_date: "",
  phishing_license_expiration_date: "",
  is_active: false,
  is_used: false,
  system_license_key: "",
};

const DetailLicense = ({ id }) => {
  const { t } = useTranslation();

  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const formatDate = (value) => {
    if (!value) return "N/A";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString();
  };

  const formatBoolean = (value) => {
    return value ? t("Yes") : t("No");
  };

  const maskLicenseKey = (key) => {
    if (!key) return "N/A";

    if (key.length <= 8) {
      return "********";
    }

    return `${key.slice(0, 4)}********${key.slice(-4)}`;
  };

  const fetchLicenseData = async (signal) => {
    try {
      setLoading(true);

      const token = localStorage.getItem("userToken");

      const response = await fetch(`${api_url}/licenses/${id}`, {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed with status ${response.status}`);
      }

      const data = await response.json();

      setFormData({
        customer_id: data?.customer_id ?? "",
        system_license_max_users: data?.system_license_max_users ?? "",
        system_license_start_date: data?.system_license_start_date ?? "",
        system_license_expiration_date: data?.system_license_expiration_date ?? "",
        phishing_license_max_users: data?.phishing_license_max_users ?? "",
        phishing_license_start_date: data?.phishing_license_start_date ?? "",
        phishing_license_expiration_date: data?.phishing_license_expiration_date ?? "",
        is_active: Boolean(data?.is_active),
        is_used: Boolean(data?.is_used),
        system_license_key: data?.system_license_key ?? "",
      });
    } catch (error) {
      if (error.name !== "AbortError") {
        showToast(t("errorFetchingLicenseDetails"), "error");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!show || !id) return;

    const controller = new AbortController();
    fetchLicenseData(controller.signal);

    return () => {
      controller.abort();
    };
  }, [show, id]);

  return (
    <div>
      <div
        className="iconEdit"
        onClick={handleShow}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleShow();
          }
        }}
      >
        {t("LicenseDetail")}
      </div>

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title className="pageTitle">{t("LicenseDetail")}</Modal.Title>
        </Modal.Header>

        <Modal.Body className="addUserWrapper">
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" />
              <div className="mt-2">{t("Loading")}...</div>
            </div>
          ) : (
            <div className="container">
              <div className="row mb-3">
                <div className="col-12">
                  <label className="form-label">{t("Licensekey")}</label>
                  <p className="form-control-plaintext">
                    {maskLicenseKey(formData.system_license_key)}
                  </p>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t("Customer")}</label>
                  <p className="form-control-plaintext">
                    {formData.customer_id || "N/A"}
                  </p>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">{t("Used")}</label>
                  <p className="form-control-plaintext">
                    {formatBoolean(formData.is_used)}
                  </p>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t("LicenseMaxUsers")}</label>
                  <p className="form-control-plaintext">
                    {formData.system_license_max_users || "N/A"}
                  </p>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">{t("LicenseStartDate")}</label>
                  <p className="form-control-plaintext">
                    {formatDate(formData.system_license_start_date)}
                  </p>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t("LicenseExpirationDate")}</label>
                  <p className="form-control-plaintext">
                    {formatDate(formData.system_license_expiration_date)}
                  </p>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">{t("PhishingMaxUsers")}</label>
                  <p className="form-control-plaintext">
                    {formData.phishing_license_max_users || "N/A"}
                  </p>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t("PhishingStartDate")}</label>
                  <p className="form-control-plaintext">
                    {formatDate(formData.phishing_license_start_date)}
                  </p>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">{t("PhishingExpirationDate")}</label>
                  <p className="form-control-plaintext">
                    {formatDate(formData.phishing_license_expiration_date)}
                  </p>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t("Active")}</label>
                  <p className="form-control-plaintext">
                    {formatBoolean(formData.is_active)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            {t("Close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DetailLicense;